import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client - same instance as Community Portal
const SUPABASE_URL = 'https://dlmcxxzohervnbixauhs.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRsbWN4eHpvaGVydm5iaXhhdWhzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDUxNzcwNzEsImV4cCI6MjA2MDc1MzA3MX0.UIQ7Wqo3R9aTYHYQHTFsHWGdhFFKLHjhEShlz2J2J-Q';

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

/**
 * Fallback mapping for legacy configs without center_id
 * Only used when config doesn't provide a center_id UUID
 */
const LEGACY_CENTER_MAPPING = {
  'dez-lectures': '8f325937-a65c-469e-9931-4be380176e62', // Dar-e-Zainab
  'dar-e-zainab': '8f325937-a65c-469e-9931-4be380176e62',
  // Add more mappings as needed for centers without center_id in config
};

/**
 * Get the database UUID for a center
 * @param {string} centerId - The center ID from config (UUID or legacy ID)
 * @returns {string} The database UUID
 */
function getCenterUUID(centerId) {
  // If it's already a UUID format, return as-is
  if (centerId && centerId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
    return centerId;
  }
  
  // Fall back to legacy mapping for configs without center_id
  console.warn(`Using legacy mapping for center: ${centerId}`);
  return LEGACY_CENTER_MAPPING[centerId] || centerId;
}

/**
 * Community Service - Handles all community-related data fetching
 */
export const communityService = {
  /**
   * Fetch announcements for a specific center
   * @param {string} centerId - The center ID to fetch announcements for
   * @returns {Promise<Array>} Array of announcements
   */
  async getAnnouncements(centerId) {
    try {
      // Convert config ID to database UUID
      const centerUUID = getCenterUUID(centerId);
      console.log('Fetching announcements for:', centerId, '-> UUID:', centerUUID);
      
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .eq('center_id', centerUUID)
        .eq('is_published', true)
        .or('expires_at.is.null,expires_at.gt.now()')
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching announcements:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAnnouncements:', error);
      return [];
    }
  },

  /**
   * Fetch expired/archived announcements for a center
   * @param {string} centerId - The center ID
   * @returns {Promise<Array>} Expired announcements, most recently expired first
   */
  async getExpiredAnnouncements(centerId) {
    try {
      const centerUUID = getCenterUUID(centerId);
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .eq('center_id', centerUUID)
        .eq('is_published', true)
        .not('expires_at', 'is', null)
        .lt('expires_at', new Date().toISOString())
        .order('expires_at', { ascending: false })
        .limit(20); // Limit to recent 20 expired

      if (error) {
        console.error('Error fetching expired announcements:', error);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error in getExpiredAnnouncements:', error);
      return [];
    }
  },

  /**
   * Fetch a single announcement by ID
   * @param {string} announcementId - The announcement ID
   * @returns {Promise<Object|null>} Announcement object or null
   */
  async getAnnouncementById(announcementId) {
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .eq('id', announcementId)
        .single();

      if (error) {
        console.error('Error fetching announcement:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getAnnouncementById:', error);
      return null;
    }
  },

  /**
   * Increment view count for an announcement
   * @param {string} announcementId - The announcement ID
   */
  async incrementViewCount(announcementId) {
    try {
      const { error } = await supabase.rpc('increment_view_count', {
        announcement_id: announcementId
      });

      if (error) {
        console.error('Error incrementing view count:', error);
      }
    } catch (error) {
      console.error('Error in incrementViewCount:', error);
    }
  },

  /**
   * Fetch calendar events for a specific center (placeholder for future)
   * @param {string} centerId - The center ID
   * @returns {Promise<Array>} Array of calendar events
   */
  async getCalendarEvents(centerId) {
    try {
      const { data, error } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('center_id', centerId)
        .gte('date', new Date().toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (error) {
        console.error('Error fetching calendar events:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getCalendarEvents:', error);
      return [];
    }
  },

  /**
   * Fetch Q&A questions for a specific center (placeholder for future)
   * @param {string} centerId - The center ID
   * @param {boolean} answeredOnly - Whether to fetch only answered questions
   * @returns {Promise<Array>} Array of questions
   */
  async getQuestions(centerId, answeredOnly = true) {
    try {
      let query = supabase
        .from('qa_questions')
        .select(`
          *,
          qa_answers (*)
        `)
        .eq('center_id', centerId);

      if (answeredOnly) {
        query = query.eq('is_answered', true);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching questions:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getQuestions:', error);
      return [];
    }
  },

  /**
   * Toggle a reaction for an announcement (anonymous version)
   * Uses sessionStorage to track reactions per device/session
   * @param {string} announcementId - The announcement ID
   * @param {string} sessionId - A unique session identifier
   * @param {string} reactionType - The type of reaction (e.g., 'like')
   * @returns {Promise<Object>} Result object with action taken
   */
  async toggleReaction(announcementId, sessionId, reactionType) {
    try {
      // For anonymous reactions, we use the session_id column instead of user_id
      // Check if reaction already exists for this session
      const { data: existing, error: checkError } = await supabase
        .from('announcement_reactions')
        .select('id')
        .eq('announcement_id', announcementId)
        .eq('session_id', sessionId)
        .eq('reaction_type', reactionType)
        .is('user_id', null) // Ensure we're checking anonymous reactions
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking reaction:', checkError);
        throw checkError;
      }

      if (existing) {
        // Remove reaction if it exists
        const { error: deleteError } = await supabase
          .from('announcement_reactions')
          .delete()
          .eq('id', existing.id);

        if (deleteError) {
          console.error('Error removing reaction:', deleteError);
          throw deleteError;
        }
        
        console.log('Reaction removed:', { announcementId, sessionId, reactionType });
        return { action: 'removed' };
      } else {
        // Add new reaction using session_id for anonymous users
        const { data, error: insertError } = await supabase
          .from('announcement_reactions')
          .insert({
            announcement_id: announcementId,
            session_id: sessionId, // Using session_id column for anonymous reactions
            reaction_type: reactionType,
            // user_id is left null for anonymous reactions
          })
          .select()
          .single();

        if (insertError) {
          console.error('Error adding reaction:', insertError);
          throw insertError;
        }
        
        console.log('Reaction added:', { announcementId, sessionId, reactionType });
        return { action: 'added', data };
      }
    } catch (error) {
      console.error('Error in toggleReaction:', error);
      throw error;
    }
  },

  /**
   * Get reaction counts for multiple announcements
   * @param {Array<string>} announcementIds - Array of announcement IDs
   * @returns {Promise<Object>} Object with reaction counts per announcement
   */
  async getReactionCounts(announcementIds) {
    try {
      const { data, error } = await supabase
        .from('announcement_reactions')
        .select('announcement_id, reaction_type')
        .in('announcement_id', announcementIds);

      if (error) {
        console.error('Error fetching reaction counts:', error);
        return {};
      }

      // Count reactions by type for each announcement
      const counts = {};
      (data || []).forEach(reaction => {
        if (!counts[reaction.announcement_id]) {
          counts[reaction.announcement_id] = {};
        }
        counts[reaction.announcement_id][reaction.reaction_type] = 
          (counts[reaction.announcement_id][reaction.reaction_type] || 0) + 1;
      });

      return counts;
    } catch (error) {
      console.error('Error in getReactionCounts:', error);
      return {};
    }
  },

  /**
   * Get user's reactions for multiple announcements
   * @param {string} sessionId - The session ID
   * @param {Array<string>} announcementIds - Array of announcement IDs
   * @returns {Promise<Array>} Array of user reactions
   */
  async getUserReactions(sessionId, announcementIds) {
    try {
      // For anonymous users, check the session_id column
      const { data, error } = await supabase
        .from('announcement_reactions')
        .select('announcement_id, reaction_type')
        .eq('session_id', sessionId)
        .is('user_id', null) // Ensure we're getting anonymous reactions
        .in('announcement_id', announcementIds);

      if (error) {
        console.error('Error fetching user reactions:', error);
        return [];
      }

      // Group reactions by announcement
      const grouped = (data || []).reduce((acc, reaction) => {
        const announcementId = reaction.announcement_id;
        if (!acc[announcementId]) {
          acc[announcementId] = {
            announcement_id: announcementId,
            reaction_types: [],
          };
        }
        acc[announcementId].reaction_types.push(reaction.reaction_type);
        return acc;
      }, {});

      return Object.values(grouped);
    } catch (error) {
      console.error('Error in getUserReactions:', error);
      return [];
    }
  }
};

export default communityService;